import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  IconButton,
  Drawer,
  InputAdornment,
  Container,
  useTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Fab,
  TablePagination,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import SupportIcon from '@mui/icons-material/Support';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { environment } from '../../environments/environment';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import botIcon from '../../assets/Chatbot.svg';
import { Chatbot } from './Chatbot/components/Chatbot';
import AddIcon from '@mui/icons-material/Add';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import ReactMarkdown from 'react-markdown';

// Types and Interfaces
interface ChatMessage {
  id: number;
  text: string | any;
  sender: 'user' | 'bot';
  timestamp: Date;
}

interface MessageBubbleProps {
  message: ChatMessage;
  formatMessageContent: (message: ChatMessage) => React.ReactNode;
  selectedCustomer?: Customer | null;
  isFullScreen: boolean;
  setIsFullScreen: (value: boolean) => void;
}

interface Customer {
  docUID: number;
  entries: {
    key: string;
    label: string;
    value: any;
  }[];
}

interface Entry {
  key: string;
  label: string;
  value: any;
}

interface Project {
  id: string;
  org_id: string;
  project_name: string;
  project_description: string;
  project_type: string;
  configuration: {
    period_start_date: string;
    period_end_date: string;
    period_type: string;
  };
  source: string;
  active: boolean;
  status: string;
  last_updated_date: string;
}

interface Service {
  name: string;
  amount: number;
}

interface Charge {
  type: string;
  amount: number;
  usage?: number;
}

interface ContractValue {
  plan?: string;
  status?: string;
  activation_date?: string;
  base_plan_cost?: number;
  total_monthly_commitment?: number;
  termination_date?: string;
  recurring_service_costs: Service[];
}

interface InvoiceValue {
  invoice_id?: string;
  billing_period?: string;
  total?: number;
  charges: Charge[];
}

// Add type guard functions
const isContractValue = (value: any): value is ContractValue => {
  return value && typeof value === 'object' && 'recurring_service_costs' in value;
};

const isInvoiceValue = (value: any): value is InvoiceValue => {
  return value && typeof value === 'object' && 'charges' in value;
};

// Styled Components
const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 16,
  padding: theme.spacing(4),
  height: '100%',
  boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
}));

const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'sender',
})<{ sender: 'user' | 'bot' }>(({ theme, sender }) => ({
  maxWidth: '70%',
  padding: theme.spacing(1.5, 2),
  borderRadius: sender === 'user' ? '12px 4px 12px 12px' : '4px 12px 12px 12px',
  backgroundColor: sender === 'user' ? '#4c3794' : '#F5F5F5',
  color: sender === 'user' ? '#FFFFFF' : '#1a1a1a',
  marginLeft: sender === 'user' ? 'auto' : '16px',
  marginRight: sender === 'user' ? '16px' : 'auto',
  marginBottom: theme.spacing(1),
  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
  position: 'relative',
  fontSize: '0.75rem',
  lineHeight: 1.4,
  '&:hover': {
    transform: 'translateY(-1px)',
    transition: 'transform 0.2s ease',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
  }
}));

const ChatDrawer = styled(Drawer)({
  '& .MuiDrawer-paper': {
    width: '400px',
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
});

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  margin: '8px 0',
  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  borderRadius: '12px !important',
  border: '1px solid #E0E0E0',
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: '8px 0',
    backgroundColor: 'rgba(76, 55, 148, 0.1)',
    borderColor: '#4c3794',
  },
  '&.selected': {
    backgroundColor: 'rgba(76, 55, 148, 0.1)',
    borderColor: '#4c3794',
  }
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  borderRadius: '12px',
  padding: '4px 8px',
  '&.Mui-expanded': {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    backgroundColor: '#F5F5F5',
  },
  '& .MuiAccordionSummary-content': {
    margin: '12px 0',
  }
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: '24px',
  backgroundColor: '#FFFFFF',
  borderBottomLeftRadius: '12px',
  borderBottomRightRadius: '12px',
}));

const FullScreenContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'white',
  zIndex: theme.zIndex.modal,
  padding: '24px'
}));

const FloatingBotButton = styled(IconButton)({
  position: 'fixed',
  bottom: '24px',
  right: '24px',
  width: '56px',
  height: '56px',
  borderRadius: '28px',
  backgroundColor: '#4c3794',
  boxShadow: '0 4px 12px rgba(76, 55, 148, 0.3)',
  '&:hover': {
    backgroundColor: '#3a2a73',
  },
  '& svg': {
    color: '#FFFFFF',
    fontSize: '24px',
  },
});

const MessageBubbleComponent: React.FC<MessageBubbleProps> = ({
  message,
  formatMessageContent,
  selectedCustomer,
  isFullScreen,
  setIsFullScreen
}) => {
  return (
    <MessageBubble sender={message.sender}>
      <Box sx={{ position: 'relative' }}>
        {formatMessageContent(message)}
        <Typography variant="caption" sx={{
          display: 'block',
          mt: 1,
          color: '#64748B',
          fontSize: '0.7rem',
          textAlign: message.sender === 'user' ? 'right' : 'left',
          opacity: 0.8
        }}>
          {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </Typography>
      </Box>
    </MessageBubble>
  );
};

// Add MarkdownMessage component
const MarkdownMessage = ({ text }: { text: string | undefined }) => {
  if (!text) return null;
  return (
    <ReactMarkdown
      components={{
        strong: ({ children }) => (
          <Typography component="span" sx={{ fontWeight: 'bold' }}>
            {children}
          </Typography>
        ),
        em: ({ children }) => (
          <Typography component="span" sx={{ fontStyle: 'italic' }}>
            {children}
          </Typography>
        ),
        del: ({ children }) => (
          <Typography component="span" sx={{ textDecoration: 'line-through' }}>
            {children}
          </Typography>
        ),
        p: ({ children }) => (
          <Typography component="span" sx={{ display: 'inline' }}>
            {children}
          </Typography>
        ),
      }}
    >
      {text}
    </ReactMarkdown>
  );
};

const CustomerService = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const navigate = useNavigate();

  // Initial loading state to track all API calls
  const [initialLoadingState, setInitialLoadingState] = useState({
    projects: false,
    customers: false
  });

  // Compute overall initial loading status
  const isInitialLoading = useMemo(() => {
    return initialLoadingState.projects || initialLoadingState.customers;
  }, [initialLoadingState]);

  // Ref to track previous config to prevent unnecessary Chatbot resets
  const previousConfigRef = useRef<string>('');
  const chatbotApiConfigRef = useRef<{
    baseUrl: string;
    queryEndpoint: string;
    campaignId: string;
    agentCode: string;
    contentTypesEndpoint: string;
    generateContentEndpoint: string;
    customQueryParams: Record<string, any>;
  }>({
    baseUrl: environment.controlplaneUrl,
    queryEndpoint: '/agent/flow/execute',
    campaignId: '',
    agentCode: 'ANALYZE_SERVICE_QUERY_AND_RESPOND',
    contentTypesEndpoint: '/account/getContentTypes',
    generateContentEndpoint: '/agent/flow/execute',
    customQueryParams: {}
  });

  // Memoize apiConfig to prevent unnecessary Chatbot resets
  // Dynamically set agent code and docUID based on selectedCustomer
  const chatbotApiConfig = useMemo(() => {
    const currentCampaignId = selectedProject || '';
    const currentDocUID = selectedCustomer?.docUID || '';
    const configKey = `${currentCampaignId}_${currentDocUID}`;

    // Only create new object if config actually changed
    if (previousConfigRef.current === configKey) {
      return chatbotApiConfigRef.current;
    }

    // Config changed, update refs and return new config
    previousConfigRef.current = configKey;
    const customQueryParams: Record<string, any> = {};
    if (currentDocUID) {
      customQueryParams.docUID = currentDocUID;
    }

    const newConfig = {
      baseUrl: environment.controlplaneUrl,
      queryEndpoint: '/agent/flow/execute',
      campaignId: currentCampaignId,
      agentCode: 'ANALYZE_SERVICE_QUERY_AND_RESPOND',
      contentTypesEndpoint: '/account/getContentTypes',
      generateContentEndpoint: '/agent/flow/execute',
      customQueryParams
    };
    chatbotApiConfigRef.current = newConfig;
    return newConfig;
  }, [selectedProject, selectedCustomer?.docUID]);

  const handleCloseChat = () => {
    setIsChatOpen(false);
  };

  const handleChatClick = () => {
    setIsChatOpen(true);
  };

  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [expandedPanel, setExpandedPanel] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(6);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (projects.length > 0 && !selectedProject) {
      setSelectedProject(projects[0].id);
    }
  }, [projects]);

  useEffect(() => {
    if (selectedProject) {
      fetchCustomers();
    }
  }, [selectedProject]);

  useEffect(() => {
    // Add keyframes for typing animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes bounce {
        0%, 100% {
          transform: translateY(0);
        }
        50% {
          transform: translateY(-4px);
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Old chat functions removed - now using reusable Chatbot component
  // The Chatbot component handles all messaging internally

  const fetchProjects = async () => {
    setLoading(true);
    setInitialLoadingState(prev => ({ ...prev, projects: true }));
    try {
      const response = await axios.post(
        `${environment.apiUrl}/workspace/getProjectForOrg`,
        {
          data: {
            org_id: localStorage.getItem('org_id'),
            project_type: ["SRVC_AGNT"]
          },
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );
      setProjects(response.data || []);
    } catch (err: any) {
      if (err?.response?.data?.error === 'jwt malformed' || err?.response?.data?.error === 'jwt expired') {
        navigate('/login');
      }
      setError('Failed to fetch projects. Please try again.');
      console.error('Error fetching projects:', err);
    } finally {
      setLoading(false);
      setInitialLoadingState(prev => ({ ...prev, projects: false }));
    }
  };

  const fetchCustomers = async () => {
    setLoading(true);
    setInitialLoadingState(prev => ({ ...prev, customers: true }));
    try {
      const response = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        {
          data: {
            topic: "",
            keywords: [],
            contentType: "USER",
            pageNumber: 1,
            pageSize: 500,
            campaignId: selectedProject
          },
          agent: {
            code: "QUERY_SERVICE_USERS"
          }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );
      setCustomers(response.data.data || []);
    } catch (err: any) {
      if (err?.response?.data?.error === 'jwt malformed' || err?.response?.data?.error === 'jwt expired') {
        navigate('/login');
      }
      setError('Failed to fetch customers. Please try again.');
      console.error('Error fetching customers:', err);
    } finally {
      setLoading(false);
      setInitialLoadingState(prev => ({ ...prev, customers: false }));
    }
  };

  const handleCreateProject = async () => {
    try {
      const response = await axios.post(
        `${environment.apiUrl}/workspace/updateCustomProjectForOrg`,
        {
          org_id: localStorage.getItem('org_id'),
          source: "10_org",
          project_items: [
            {
              project_name: newProjectName,
              project_description: newProjectDescription,
              project_type: "SRVC_AGNT",
              configuration: {
                period_start_date: new Date().toISOString().split('T')[0],
                period_end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                period_type: "D"
              }
            }
          ]
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.upsertCount > 0) {
        setSuccess('Project created successfully');
        await fetchProjects();
        if (response.data.upsertRef && response.data.upsertRef.length > 0) {
          setSelectedProject(response.data.upsertRef[0]);

          // Auto-assign permissions for the new project
          try {
            const autoAssignResponse = await axios.post(
              `${environment.apiUrl}/manage/project-permissions/auto-assign`,
              {
                projectId: response.data.upsertRef[0],
                userId: localStorage.getItem('user_email') || 'admin@example.com',
                organisationId: localStorage.getItem('org_id') || 'org123'
              },
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('token')}`,
                  'Content-Type': 'application/json',
                },
              }
            );

            console.log('✅ Auto-assign permissions successful:', autoAssignResponse.data);

          } catch (autoAssignError: any) {
            console.error('⚠️ Auto-assign permissions failed:', autoAssignError);
            // Don't show error to user as project creation was successful
            // Auto-assign is a convenience feature, not critical
          }
        }
        setOpenCreateDialog(false);
        setNewProjectName('');
        setNewProjectDescription('');
      }
    } catch (err: any) {
      if (err?.response?.data?.error === 'jwt malformed' || err?.response?.data?.error === 'jwt expired') {
        navigate('/login');
      }
      setError('Failed to create project. Please try again.');
      console.error('Error creating project:', err);
    }
  };

  const handleCustomerSelect = (docUID: number) => {
    const customer = customers.find(c => c.docUID === docUID) || null;
    setSelectedCustomer(customer);
    // Chat state is now managed by the reusable Chatbot component
    // No need to reset messages
  };

  return (
    <Box sx={{ padding: '10px' }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        marginBottom: '24px',
        justifyContent: 'space-between'
      }}>
        {/* Left: Back, icon, title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIosIcon sx={{ fontSize: 18 }} />}
            onClick={() => {
              // Check if we have a stored navigation context
              const context = sessionStorage.getItem('appsNavigationContext');
              if (context && context.startsWith('industry:')) {
                // Navigate back to Apps with the stored context
                navigate('/Apps');
              } else {
                // Default navigation to Apps
                navigate('/Apps');
              }
            }}
            sx={{
              color: '#4c3794',
              fontWeight: 600,
              fontSize: '0.875rem',
              '&:hover': {
                backgroundColor: 'rgba(76, 55, 148, 0.1)'
              }
            }}
          >
            Back
          </Button>
          <SupportIcon sx={{ fontSize: 24, color: '#4c3794', marginLeft: '24px' }} />
          <Typography variant="h5" sx={{
            color: '#131F32',
            fontWeight: 600,
            fontSize: '1.25rem',
            background: 'linear-gradient(45deg, #131F32, #4c3794)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Customer Service AI
          </Typography>
        </Box>
        {/* Right: Dropdown and button inline */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl sx={{ minWidth: 220 }} size="small">
            <InputLabel>Select Project</InputLabel>
            <Select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              label="Select Project"
              disabled={loading}
            >
              {projects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.project_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenCreateDialog(true)}
            sx={{
              backgroundColor: '#4c3794',
              '&:hover': { backgroundColor: '#3a2a73' },
              fontWeight: 600
            }}
          >
            Create Project
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2}>
        {/* Left Column - Customer List */}
        {!isFullScreen && (
          <Grid item xs={12} md={isChatOpen ? 8 : 12} sx={{ width: isChatOpen ? '65%' : '100%', transition: 'width 0.3s ease-in-out', position: 'relative' }}>
            {isInitialLoading && (
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '400px',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                zIndex: 10,
                borderRadius: '16px'
              }}>
                <Box sx={{ textAlign: 'center' }}>
                  <CircularProgress sx={{ color: '#4c3794', mb: 2 }} size={50} />
                  <Typography sx={{ color: '#4c3794', fontWeight: 600 }}>
                    Loading data...
                  </Typography>
                </Box>
              </Box>
            )}
            <StyledPaper sx={{
              border: '1px solid #E0E0E0',
              borderRadius: '16px',
              height: 'calc(100vh - 180px)',
              overflow: 'hidden',
              transition: 'box-shadow 0.3s ease',
              '&:hover': {
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
              },
              opacity: isInitialLoading ? 0.3 : 1
            }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2.5,
                borderBottom: '1px solid #E0E0E0',
                background: 'linear-gradient(145deg, #FFFFFF 0%, #F8F9FA 100%)',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <SupportIcon sx={{ color: '#4c3794', fontSize: 24 }} />
                  <Typography variant="h6" sx={{
                    color: '#131F32',
                    fontWeight: 600,
                    fontSize: '1.125rem'
                  }}>
                    Customers
                    <Chip
                      label={`${customers?.length || 0} customers`}
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(76, 55, 148, 0.1)',
                        color: '#4c3794',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        marginLeft: '10px'
                      }}
                    />
                  </Typography>
                </Box>
              </Box>

              <Box sx={{
                height: 'calc(100% - 76px)',
                overflow: 'auto',
                p: 2.5,
                backgroundColor: '#FAFBFC'
              }}>
                {customers.length === 0 ? (
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 2,
                    height: '100%',
                    backgroundColor: '#FFFFFF',
                    borderRadius: '12px',
                    border: '1px dashed #E0E0E0',
                    py: 8
                  }}>
                    <SupportIcon sx={{
                      fontSize: 48,
                      color: '#9E9E9E',
                      opacity: 0.5
                    }} />
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" sx={{
                        color: '#131F32',
                        fontWeight: 600,
                        mb: 1
                      }}>
                        No customers found
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#64748B' }}>
                        Try selecting a different project or check back later
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <>
                    {customers
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((customer) => {
                        const subscriberName = customer.entries.find(e => e.key === 'subscriber_name')?.value || 'Unknown';
                        const accountId = customer.entries.find(e => e.key === 'account_id')?.value || '';
                        const contract = customer.entries.find(e => e.key === 'contract')?.value || {};
                        const status = contract.status || 'Unknown';

                        // Filter and process entries
                        const filteredEntries = customer.entries
                          .filter(entry =>
                            entry.key !== 'subscriber_name' &&
                            entry.key !== 'account_id' &&
                            entry.key !== 'campaignId' &&
                            entry.key !== 'currentDate' &&
                            entry.key !== 'orgCode'
                          );

                        // Separate entries with data and without data
                        const entriesWithData = filteredEntries.filter(entry =>
                          entry.value !== 'NA' &&
                          entry.value !== null &&
                          entry.value !== undefined &&
                          entry.value !== ''
                        );

                        const entriesWithoutData = filteredEntries.filter(entry =>
                          entry.value === 'NA' ||
                          entry.value === null ||
                          entry.value === undefined ||
                          entry.value === ''
                        );

                        return (
                          <StyledAccordion
                            key={customer.docUID}
                            expanded={expandedPanel === customer.docUID}
                            onChange={(event, isExpanded) => {
                              setExpandedPanel(isExpanded ? customer.docUID : null);
                              handleCustomerSelect(customer.docUID);
                            }}
                            className={selectedCustomer?.docUID === customer.docUID ? 'selected' : ''}
                            sx={{
                              '&:not(:last-child)': {
                                mb: 2
                              }
                            }}
                          >
                            <StyledAccordionSummary
                              expandIcon={
                                <ExpandMoreIcon sx={{
                                  color: '#4c3794',
                                  transform: expandedPanel === customer.docUID ? 'rotate(-180deg)' : 'rotate(0)',
                                  transition: 'transform 0.3s ease'
                                }} />
                              }
                            >
                              <Box sx={{ width: '100%' }}>
                                <Box sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'flex-start',
                                  mb: 1.5
                                }}>
                                  <Typography variant="subtitle1" sx={{
                                    fontWeight: 600,
                                    color: '#1A2027',
                                    lineHeight: 1.4,
                                    pr: 2
                                  }}>
                                    {subscriberName}
                                  </Typography>
                                  <Chip
                                    label={status}
                                    size="small"
                                    sx={{
                                      backgroundColor: status === 'Active' ? '#E8F5E9' : 'rgba(76, 55, 148, 0.1)',
                                      color: status === 'Active' ? '#2E7D32' : '#4c3794',
                                      fontSize: '0.75rem',
                                      fontWeight: 600
                                    }}
                                  />
                                </Box>
                                <Typography variant="body2" sx={{
                                  color: '#64748B',
                                  fontSize: '0.75rem'
                                }}>
                                  Account ID: {accountId}
                                </Typography>
                              </Box>
                            </StyledAccordionSummary>
                            <StyledAccordionDetails>
                              <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 3,
                                pt: 1
                              }}>
                                {(() => {
                                  // Group entries by type for better organization
                                  const contractEntries = entriesWithData.filter(entry =>
                                    entry.key.includes('contract') ||
                                    entry.key.includes('plan') ||
                                    entry.key.includes('activation') ||
                                    entry.key.includes('termination')
                                  );

                                  const invoiceEntries = entriesWithData.filter(entry =>
                                    entry.key.includes('invoice') ||
                                    entry.key.includes('billing') ||
                                    entry.key.includes('charge') ||
                                    entry.key.includes('payment')
                                  );

                                  const serviceEntries = entriesWithData.filter(entry =>
                                    entry.key.includes('service') ||
                                    entry.key.includes('recurring')
                                  );

                                  const otherEntries = entriesWithData.filter(entry =>
                                    !entry.key.includes('contract') &&
                                    !entry.key.includes('plan') &&
                                    !entry.key.includes('activation') &&
                                    !entry.key.includes('termination') &&
                                    !entry.key.includes('invoice') &&
                                    !entry.key.includes('billing') &&
                                    !entry.key.includes('charge') &&
                                    !entry.key.includes('payment') &&
                                    !entry.key.includes('service') &&
                                    !entry.key.includes('recurring')
                                  );

                                  // Group unavailable entries by type
                                  const unavailableContractEntries = entriesWithoutData.filter(entry =>
                                    entry.key.includes('contract') ||
                                    entry.key.includes('plan') ||
                                    entry.key.includes('activation') ||
                                    entry.key.includes('termination')
                                  );

                                  const unavailableInvoiceEntries = entriesWithoutData.filter(entry =>
                                    entry.key.includes('invoice') ||
                                    entry.key.includes('billing') ||
                                    entry.key.includes('charge') ||
                                    entry.key.includes('payment')
                                  );

                                  const unavailableServiceEntries = entriesWithoutData.filter(entry =>
                                    entry.key.includes('service') ||
                                    entry.key.includes('recurring')
                                  );

                                  const unavailableOtherEntries = entriesWithoutData.filter(entry =>
                                    !entry.key.includes('contract') &&
                                    !entry.key.includes('plan') &&
                                    !entry.key.includes('activation') &&
                                    !entry.key.includes('termination') &&
                                    !entry.key.includes('invoice') &&
                                    !entry.key.includes('billing') &&
                                    !entry.key.includes('charge') &&
                                    !entry.key.includes('payment') &&
                                    !entry.key.includes('service') &&
                                    !entry.key.includes('recurring')
                                  );

                                  // Helper function to render entry value
                                  const renderEntryValue = (value: any) => {
                                    if (typeof value === 'string' || typeof value === 'number') {
                                      return value.toString();
                                    } else if (Array.isArray(value)) {
                                      return value.map((item, i) =>
                                        typeof item === 'object'
                                          ? `• ${Object.entries(item)
                                            .map(([k, v]) => `${k}: ${v}`)
                                            .join(', ')}`
                                          : `• ${item}`
                                      ).join('\n');
                                    } else if (typeof value === 'object' && value !== null) {
                                      return Object.entries(value)
                                        .map(([k, v]) =>
                                          Array.isArray(v)
                                            ? `${k}:\n  ${v.map(sub =>
                                              typeof sub === 'object'
                                                ? `- ${Object.entries(sub)
                                                  .map(([sk, sv]) => `${sk}: ${sv}`)
                                                  .join(', ')}`
                                                : `- ${sub}`
                                            ).join('\n  ')}`
                                            : `${k}: ${v}`
                                        )
                                        .join('\n');
                                    }
                                    return 'N/A';
                                  };

                                  // Helper function to check if section has data
                                  const hasData = (entries: any[]) => {
                                    return entries.length > 0;
                                  };

                                  return (
                                    <>
                                      {/* Contract Information */}
                                      {(hasData(contractEntries) || hasData(unavailableContractEntries)) && (
                                        <Box sx={{
                                          backgroundColor: 'rgba(76, 55, 148, 0.05)',
                                          borderRadius: 2,
                                          p: 2.5,
                                          border: '1px solid rgba(76, 55, 148, 0.1)'
                                        }}>
                                          <Typography variant="h6" sx={{
                                            fontWeight: 600,
                                            color: '#4c3794',
                                            mb: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1
                                          }}>
                                            <Box sx={{
                                              width: 6,
                                              height: 20,
                                              backgroundColor: '#4c3794',
                                              borderRadius: 3
                                            }} />
                                            Contract Information
                                          </Typography>
                                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            {/* Available data */}
                                            {contractEntries.map((entry, entryIndex) => (
                                              <Box key={entryIndex} sx={{
                                                backgroundColor: '#fff',
                                                borderRadius: 1.5,
                                                p: 2,
                                                border: '1px solid rgba(76, 55, 148, 0.08)'
                                              }}>
                                                <Typography variant="subtitle2" sx={{
                                                  fontWeight: 600,
                                                  color: '#1A2027',
                                                  mb: 1,
                                                  fontSize: '0.875rem'
                                                }}>
                                                  {entry.label}
                                                </Typography>
                                                <Typography
                                                  variant="body2"
                                                  sx={{
                                                    color: '#475569',
                                                    lineHeight: 1.6,
                                                    whiteSpace: 'pre-wrap',
                                                    fontSize: '0.875rem'
                                                  }}
                                                >
                                                  {renderEntryValue(entry.value)}
                                                </Typography>
                                              </Box>
                                            ))}
                                            {/* Unavailable data */}
                                            {unavailableContractEntries.map((entry, entryIndex) => (
                                              <Box key={`unavailable-${entryIndex}`} sx={{
                                                backgroundColor: '#fff',
                                                borderRadius: 1.5,
                                                p: 2,
                                                border: '1px solid rgba(158, 158, 158, 0.08)'
                                              }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                  <Typography variant="subtitle2" sx={{
                                                    fontWeight: 600,
                                                    color: '#1A2027',
                                                    fontSize: '0.875rem'
                                                  }}>
                                                    {entry.label}
                                                  </Typography>
                                                  <Chip
                                                    label="Not applicable"
                                                    size="small"
                                                    sx={{
                                                      backgroundColor: 'rgba(158, 158, 158, 0.1)',
                                                      color: '#9E9E9E',
                                                      fontSize: '0.7rem',
                                                      fontWeight: 500,
                                                      height: '20px'
                                                    }}
                                                  />
                                                </Box>
                                              </Box>
                                            ))}
                                          </Box>
                                        </Box>
                                      )}

                                      {/* Invoice & Billing Information */}
                                      {(hasData(invoiceEntries) || hasData(unavailableInvoiceEntries)) && (
                                        <Box sx={{
                                          backgroundColor: 'rgba(76, 55, 148, 0.05)',
                                          borderRadius: 2,
                                          p: 2.5,
                                          border: '1px solid rgba(76, 55, 148, 0.1)'
                                        }}>
                                          <Typography variant="h6" sx={{
                                            fontWeight: 600,
                                            color: '#4c3794',
                                            mb: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1
                                          }}>
                                            <Box sx={{
                                              width: 6,
                                              height: 20,
                                              backgroundColor: '#4c3794',
                                              borderRadius: 3
                                            }} />
                                            Invoice & Billing
                                          </Typography>
                                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            {/* Available data */}
                                            {invoiceEntries.map((entry, entryIndex) => (
                                              <Box key={entryIndex} sx={{
                                                backgroundColor: '#fff',
                                                borderRadius: 1.5,
                                                p: 2,
                                                border: '1px solid rgba(76, 55, 148, 0.08)'
                                              }}>
                                                <Typography variant="subtitle2" sx={{
                                                  fontWeight: 600,
                                                  color: '#1A2027',
                                                  mb: 1,
                                                  fontSize: '0.875rem'
                                                }}>
                                                  {entry.label}
                                                </Typography>
                                                <Typography
                                                  variant="body2"
                                                  sx={{
                                                    color: '#475569',
                                                    lineHeight: 1.6,
                                                    whiteSpace: 'pre-wrap',
                                                    fontSize: '0.875rem'
                                                  }}
                                                >
                                                  {renderEntryValue(entry.value)}
                                                </Typography>
                                              </Box>
                                            ))}
                                            {/* Unavailable data */}
                                            {unavailableInvoiceEntries.map((entry, entryIndex) => (
                                              <Box key={`unavailable-${entryIndex}`} sx={{
                                                backgroundColor: '#fff',
                                                borderRadius: 1.5,
                                                p: 2,
                                                border: '1px solid rgba(158, 158, 158, 0.08)'
                                              }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                  <Typography variant="subtitle2" sx={{
                                                    fontWeight: 600,
                                                    color: '#1A2027',
                                                    fontSize: '0.875rem'
                                                  }}>
                                                    {entry.label}
                                                  </Typography>
                                                  <Chip
                                                    label="Not applicable"
                                                    size="small"
                                                    sx={{
                                                      backgroundColor: 'rgba(158, 158, 158, 0.1)',
                                                      color: '#9E9E9E',
                                                      fontSize: '0.7rem',
                                                      fontWeight: 500,
                                                      height: '20px'
                                                    }}
                                                  />
                                                </Box>
                                              </Box>
                                            ))}
                                          </Box>
                                        </Box>
                                      )}

                                      {/* Service Information */}
                                      {(hasData(serviceEntries) || hasData(unavailableServiceEntries)) && (
                                        <Box sx={{
                                          backgroundColor: 'rgba(76, 175, 80, 0.05)',
                                          borderRadius: 2,
                                          p: 2.5,
                                          border: '1px solid rgba(76, 175, 80, 0.1)'
                                        }}>
                                          <Typography variant="h6" sx={{
                                            fontWeight: 600,
                                            color: '#4CAF50',
                                            mb: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1
                                          }}>
                                            <Box sx={{
                                              width: 6,
                                              height: 20,
                                              backgroundColor: '#4CAF50',
                                              borderRadius: 3
                                            }} />
                                            Service Details
                                          </Typography>
                                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            {/* Available data */}
                                            {serviceEntries.map((entry, entryIndex) => (
                                              <Box key={entryIndex} sx={{
                                                backgroundColor: '#fff',
                                                borderRadius: 1.5,
                                                p: 2,
                                                border: '1px solid rgba(76, 175, 80, 0.08)'
                                              }}>
                                                <Typography variant="subtitle2" sx={{
                                                  fontWeight: 600,
                                                  color: '#1A2027',
                                                  mb: 1,
                                                  fontSize: '0.875rem'
                                                }}>
                                                  {entry.label}
                                                </Typography>
                                                <Typography
                                                  variant="body2"
                                                  sx={{
                                                    color: '#475569',
                                                    lineHeight: 1.6,
                                                    whiteSpace: 'pre-wrap',
                                                    fontSize: '0.875rem'
                                                  }}
                                                >
                                                  {renderEntryValue(entry.value)}
                                                </Typography>
                                              </Box>
                                            ))}
                                            {/* Unavailable data */}
                                            {unavailableServiceEntries.map((entry, entryIndex) => (
                                              <Box key={`unavailable-${entryIndex}`} sx={{
                                                backgroundColor: '#fff',
                                                borderRadius: 1.5,
                                                p: 2,
                                                border: '1px solid rgba(158, 158, 158, 0.08)'
                                              }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                  <Typography variant="subtitle2" sx={{
                                                    fontWeight: 600,
                                                    color: '#1A2027',
                                                    fontSize: '0.875rem'
                                                  }}>
                                                    {entry.label}
                                                  </Typography>
                                                  <Chip
                                                    label="Not applicable"
                                                    size="small"
                                                    sx={{
                                                      backgroundColor: 'rgba(158, 158, 158, 0.1)',
                                                      color: '#9E9E9E',
                                                      fontSize: '0.7rem',
                                                      fontWeight: 500,
                                                      height: '20px'
                                                    }}
                                                  />
                                                </Box>
                                              </Box>
                                            ))}
                                          </Box>
                                        </Box>
                                      )}

                                      {/* Other Information */}
                                      {(hasData(otherEntries) || hasData(unavailableOtherEntries)) && (
                                        <Box sx={{
                                          backgroundColor: 'rgba(158, 158, 158, 0.05)',
                                          borderRadius: 2,
                                          p: 2.5,
                                          border: '1px solid rgba(158, 158, 158, 0.1)'
                                        }}>
                                          <Typography variant="h6" sx={{
                                            fontWeight: 600,
                                            color: '#9E9E9E',
                                            mb: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1
                                          }}>
                                            <Box sx={{
                                              width: 6,
                                              height: 20,
                                              backgroundColor: '#9E9E9E',
                                              borderRadius: 3
                                            }} />
                                            Additional Information
                                          </Typography>
                                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            {/* Available data */}
                                            {otherEntries.map((entry, entryIndex) => (
                                              <Box key={entryIndex} sx={{
                                                backgroundColor: '#fff',
                                                borderRadius: 1.5,
                                                p: 2,
                                                border: '1px solid rgba(158, 158, 158, 0.08)'
                                              }}>
                                                <Typography variant="subtitle2" sx={{
                                                  fontWeight: 600,
                                                  color: '#1A2027',
                                                  mb: 1,
                                                  fontSize: '0.875rem'
                                                }}>
                                                  {entry.label}
                                                </Typography>
                                                <Typography
                                                  variant="body2"
                                                  sx={{
                                                    color: '#475569',
                                                    lineHeight: 1.6,
                                                    whiteSpace: 'pre-wrap',
                                                    fontSize: '0.875rem'
                                                  }}
                                                >
                                                  {renderEntryValue(entry.value)}
                                                </Typography>
                                              </Box>
                                            ))}
                                            {/* Unavailable data */}
                                            {unavailableOtherEntries.map((entry, entryIndex) => (
                                              <Box key={`unavailable-${entryIndex}`} sx={{
                                                backgroundColor: '#fff',
                                                borderRadius: 1.5,
                                                p: 2,
                                                border: '1px solid rgba(158, 158, 158, 0.08)'
                                              }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                  <Typography variant="subtitle2" sx={{
                                                    fontWeight: 600,
                                                    color: '#1A2027',
                                                    fontSize: '0.875rem'
                                                  }}>
                                                    {entry.label}
                                                  </Typography>
                                                  <Chip
                                                    label="Not applicable"
                                                    size="small"
                                                    sx={{
                                                      backgroundColor: 'rgba(158, 158, 158, 0.1)',
                                                      color: '#9E9E9E',
                                                      fontSize: '0.7rem',
                                                      fontWeight: 500,
                                                      height: '20px'
                                                    }}
                                                  />
                                                </Box>
                                              </Box>
                                            ))}
                                          </Box>
                                        </Box>
                                      )}

                                      {/* Show message if no data available */}
                                      {!hasData(contractEntries) && !hasData(invoiceEntries) && !hasData(serviceEntries) && !hasData(otherEntries) &&
                                        !hasData(unavailableContractEntries) && !hasData(unavailableInvoiceEntries) && !hasData(unavailableServiceEntries) && !hasData(unavailableOtherEntries) && (
                                          <Box sx={{
                                            textAlign: 'center',
                                            py: 4,
                                            backgroundColor: 'rgba(158, 158, 158, 0.05)',
                                            borderRadius: 2,
                                            border: '1px dashed rgba(158, 158, 158, 0.3)'
                                          }}>
                                            <Typography variant="body2" sx={{
                                              color: '#9E9E9E',
                                              fontStyle: 'italic'
                                            }}>
                                              No additional information available for this customer.
                                            </Typography>
                                          </Box>
                                        )}
                                    </>
                                  );
                                })()}
                              </Box>
                            </StyledAccordionDetails>
                          </StyledAccordion>
                        );
                      })}
                    <Box sx={{
                      mt: 2,
                      display: 'flex',
                      justifyContent: 'flex-end',
                      borderTop: '1px solid #E0E0E0',
                      pt: 2
                    }}>
                      <TablePagination
                        count={customers.length}
                        page={page}
                        onPageChange={(event, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(event) => {
                          setRowsPerPage(parseInt(event.target.value, 10));
                          setPage(0);
                        }}
                        rowsPerPageOptions={[4, 5, 10, 25]}
                      />
                    </Box>
                  </>
                )}
              </Box>
            </StyledPaper>
          </Grid>
        )}

        {/* Right Column - Chat Panel */}
        {isFullScreen ? (
          <FullScreenContainer>
            {/* Fullscreen Chat Content */}
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
              <Chatbot
                placeholder="Type your message..."
                welcomeMessage={selectedCustomer ? `Hello! I'm your AI assistant. How can I help you with ${selectedCustomer.entries.find(e => e.key === 'subscriber_name')?.value || 'this customer'}?` : "Hello! I'm your AI assistant. How can I help you with customer service today?"}
                enableVoice={true}
                enableGovAPI={true}
                apiConfig={chatbotApiConfig}
                getAuthToken={async () => localStorage.getItem('token')}
              />
            </Box>
          </FullScreenContainer>
        ) : (
          isChatOpen && (
            <Grid item xs={12} md={4} sx={{ width: '35%', flexShrink: 0 }}>
              <StyledPaper sx={{
                height: 'calc(100vh - 180px)',
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid #E0E0E0',
                borderRadius: '16px',
                overflow: 'hidden',
                position: 'relative',
                p: 0
              }}>
                {/* Chat Panel Header with Close Button */}
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  p: 1.5,
                  borderBottom: '1px solid #e9ecef'
                }}>
                  <IconButton
                    onClick={handleCloseChat}
                    sx={{
                      color: '#64748B',
                      '&:hover': {
                        backgroundColor: 'rgba(76, 55, 148, 0.1)',
                        color: '#4c3794'
                      }
                    }}
                    aria-label="Close chat"
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
                {/* Chat Interface - Using Reusable Chatbot */}
                <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100% - 50px)', flex: 1, overflow: 'hidden' }}>
                  <Chatbot
                    placeholder="Type your message..."
                    welcomeMessage={selectedCustomer ? `Hello! I'm your AI assistant. How can I help you with ${selectedCustomer.entries.find(e => e.key === 'subscriber_name')?.value || 'this customer'}?` : "Hello! I'm your AI assistant. How can I help you with customer service today?"}
                    enableVoice={true}
                    enableGovAPI={true}
                    apiConfig={chatbotApiConfig}
                    getAuthToken={async () => localStorage.getItem('token')}
                  />
                </Box>
              </StyledPaper>
            </Grid>
          )
        )}
      </Grid>

      {/* Floating Chat Button - Only show when chat is not open and not in fullscreen */}
      {!isChatOpen && !isFullScreen && (
        <Fab
          color="primary"
          aria-label="Open AI chat assistant"
          onClick={handleChatClick}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            backgroundColor: '#4c3794',
            '&:hover': {
              backgroundColor: '#3a2a73',
            },
            '&:active': {
              transform: 'scale(0.95)'
            },
            zIndex: 1000,
          }}
        >
          <img src={botIcon} alt="AI Chat Assistant" style={{ width: 74, height: 74 }} />
        </Fab>
      )}
    </Box>
  );
};

export default CustomerService; 